// Habilita um meio de pagamento para um white label e escolhe QUEM o processa.
//
// O wizard caiu de 5 passos para 3: os passos de certificado e credenciais migraram para a tela
// de Processadores. Antes, cada meio de pagamento repetia as credenciais e criava um provider
// novo — era assim que um merchant Itaú com PIX + boleto + bolepix ficava com três cópias das
// mesmas credenciais. Aqui a configuração só APONTA uma credencial já cadastrada.
import { TriangleAlertIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormStepProgress } from "@/components/ui/form-step-progress"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import { cn } from "@/lib/utils"
import { useEligibleProcessors } from "@/services/admin/merchant-processors"
import {
  useAllowedMethods,
  useCreateProcessorConfigMutation,
  useProcessorConfigMetadata,
} from "@/services/admin/processor-config"
import type { ProcessorPaymentMethod } from "@/types/admin-processor-config"

const panelPrefix = "pages.adminProcessorConfig"

const TOTAL_STEPS = 3

type CreateConfigDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * White label e método já escolhidos — vem da aba de métodos do white label,
   * onde o operador já respondeu essas duas perguntas. Repeti-las aqui seria
   * pedir de novo o que a tela anterior já sabe.
   */
  initialWhiteLabelId?: string
  initialMethod?: ProcessorPaymentMethod
}

export const CreateConfigDialog = ({
  open,
  onOpenChange,
  initialWhiteLabelId,
  initialMethod,
}: CreateConfigDialogProps) => {
  const { t } = useTranslation()
  // Com os dois pré-escolhidos, o wizard abre direto em "quem processa".
  const firstStep =
    initialWhiteLabelId && initialMethod ? 2 : 0
  const [step, setStep] = useState(firstStep)

  const [whiteLabelId, setWhiteLabelId] = useState(
    initialWhiteLabelId ?? ""
  )
  const [method, setMethod] =
    useState<ProcessorPaymentMethod | null>(
      initialMethod ?? null
    )
  const [processorId, setProcessorId] = useState("")
  const [fallbackProcessorId, setFallbackProcessorId] =
    useState("")

  const { data: metadata } = useProcessorConfigMetadata()
  const createMutation = useCreateProcessorConfigMutation()

  // White labels da store/merchant logado: o operador só escolhe os SEUS.
  const {
    data: whiteLabels = [],
    isLoading: isLoadingWhiteLabels,
  } = useWhiteLabels()

  const allowedMethodsQuery = useAllowedMethods(
    // Só busca a partir do passo do método.
    step >= 1 ? whiteLabelId : ""
  )

  // Processadores do MERCHANT do white label que atendem o método escolhido.
  const {
    data: processors = [],
    isLoading: isLoadingProcessors,
  } = useEligibleProcessors(
    step >= 2 ? whiteLabelId : "",
    method ?? undefined
  )

  const whiteLabelOptions = useMemo(
    () =>
      (Array.isArray(whiteLabels) ? whiteLabels : [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [whiteLabels]
  )

  // Métodos oferecidos: união das matrizes dos processadores ∩ allowed-methods
  // do WL; erro/indisponível → FALLBACK: só as matrizes + aviso.
  const allowedFailed = allowedMethodsQuery.isError
  const stepMethods = useMemo(() => {
    const matrix = Array.from(
      new Set(
        (metadata ?? []).flatMap((meta) => meta.methods)
      )
    )
    if (allowedFailed) return matrix
    const allowed = allowedMethodsQuery.data
    if (!allowed || allowed.length === 0) return matrix
    return matrix.filter((item) => allowed.includes(item))
  }, [metadata, allowedFailed, allowedMethodsQuery.data])

  const gatewayOf = (id: string) =>
    processors.find((processor) => processor.id === id)
      ?.gateway

  const resetAndClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep(firstStep)
      setWhiteLabelId(initialWhiteLabelId ?? "")
      setMethod(initialMethod ?? null)
      setProcessorId("")
      setFallbackProcessorId("")
    }
    onOpenChange(nextOpen)
  }

  const handleCreate = () => {
    const gateway = gatewayOf(processorId)
    if (!method || !gateway) return
    createMutation.mutate(
      {
        whiteLabelId,
        gateway,
        method,
        processorId,
        // Ausente = o próprio principal, que é como o backend preenche.
        fallbackProcessorId:
          fallbackProcessorId || undefined,
      },
      { onSuccess: () => resetAndClose(false) }
    )
  }

  const canAdvance =
    (step === 0 && Boolean(whiteLabelId)) ||
    (step === 1 && Boolean(method))

  // Contingência só pode ser do MESMO processador: uma configuração Itaú não
  // cai para uma credencial Santander — os campos e o endpoint são outros.
  const fallbackOptions = processors.filter(
    (processor) =>
      processor.id !== processorId &&
      processor.gateway === gatewayOf(processorId)
  )

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent
        data-testid="create-config-dialog"
        className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>
            {t(`${panelPrefix}.create.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${panelPrefix}.create.subtitle`)}
          </DialogDescription>
        </DialogHeader>

        <FormStepProgress
          currentStep={step}
          max={TOTAL_STEPS}
        />

        {/* Área rolável dos passos: header e rodapé ficam fixos para o
            "Voltar/Próximo/Criar" nunca ser cortado em telas baixas. */}
        <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-1">
          {/* Passo 1 — white label (restrito aos WLs da store logada). */}
          {step === 0 && (
            <div className="space-y-1.5">
              <Label>
                {t(`${panelPrefix}.create.whiteLabelLabel`)}
              </Label>
              {isLoadingWhiteLabels ? (
                <Skeleton className="h-10 w-full rounded-md" />
              ) : whiteLabelOptions.length === 0 ? (
                <p
                  data-testid="create-wl-empty"
                  className="text-sm text-muted-foreground"
                >
                  {t(
                    `${panelPrefix}.create.whiteLabelEmpty`
                  )}
                </p>
              ) : (
                <Select
                  value={whiteLabelId}
                  onValueChange={setWhiteLabelId}
                >
                  <SelectTrigger
                    aria-label={t(
                      `${panelPrefix}.create.whiteLabelLabel`
                    )}
                  >
                    <SelectValue
                      placeholder={t(
                        `${panelPrefix}.form.selectPlaceholder`
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {whiteLabelOptions.map((whiteLabel) => (
                      <SelectItem
                        key={whiteLabel.uuid}
                        value={whiteLabel.uuid}
                      >
                        {whiteLabel.name}{" "}
                        <span className="font-mono text-xs text-muted-foreground">
                          ({whiteLabel.uuid})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Passo 2 — método (allowed-methods do WL, com fallback). */}
          {step === 1 && (
            <div className="space-y-3">
              {allowedFailed && (
                <p
                  data-testid="allowed-methods-fallback"
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <TriangleAlertIcon
                    className="size-3.5 shrink-0 text-amber-500"
                    aria-hidden="true"
                  />
                  {t(
                    `${panelPrefix}.create.allowedMethodsFallback`
                  )}
                </p>
              )}
              {allowedMethodsQuery.isLoading ? (
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: 2 }).map(
                    (_, index) => (
                      <Skeleton
                        key={`method-skeleton-${index}`}
                        className="h-12 w-full rounded-xl"
                      />
                    )
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {stepMethods.map((item) => (
                    <button
                      key={item}
                      type="button"
                      data-testid={`create-method-${item}`}
                      className={cn(
                        "rounded-xl border p-4 text-left text-sm font-semibold transition-colors hover:bg-muted/50",
                        method === item &&
                          "border-primary bg-primary/5"
                      )}
                      onClick={() => {
                        setMethod(item)
                        // Os elegíveis mudam com o método: manter a escolha
                        // anterior apontaria um processador que não o atende.
                        setProcessorId("")
                        setFallbackProcessorId("")
                      }}
                    >
                      {t(`${panelPrefix}.methods.${item}`, {
                        defaultValue: item,
                      })}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Passo 3 — qual credencial processa, com contingência opcional. */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>
                  {t(
                    `${panelPrefix}.create.processorLabel`
                  )}
                </Label>
                {isLoadingProcessors ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : processors.length === 0 ? (
                  <p
                    data-testid="create-processor-empty"
                    className="text-sm text-muted-foreground"
                  >
                    {t(
                      `${panelPrefix}.create.processorEmpty`
                    )}
                  </p>
                ) : (
                  <Select
                    value={processorId}
                    onValueChange={(value) => {
                      setProcessorId(value)
                      setFallbackProcessorId("")
                    }}
                  >
                    <SelectTrigger
                      aria-label={t(
                        `${panelPrefix}.create.processorLabel`
                      )}
                    >
                      <SelectValue
                        placeholder={t(
                          `${panelPrefix}.form.selectPlaceholder`
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {processors.map((processor) => (
                        <SelectItem
                          key={processor.id}
                          value={processor.id}
                        >
                          {processor.name ??
                            processor.gateway}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {processorId &&
                fallbackOptions.length > 0 && (
                  <div className="space-y-1.5">
                    <Label>
                      {t(
                        `${panelPrefix}.create.fallbackLabel`
                      )}
                    </Label>
                    <Select
                      value={fallbackProcessorId}
                      onValueChange={setFallbackProcessorId}
                    >
                      <SelectTrigger
                        aria-label={t(
                          `${panelPrefix}.create.fallbackLabel`
                        )}
                      >
                        <SelectValue
                          placeholder={t(
                            `${panelPrefix}.create.fallbackPlaceholder`
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {fallbackOptions.map(
                          (processor) => (
                            <SelectItem
                              key={processor.id}
                              value={processor.id}
                            >
                              {processor.name ??
                                processor.gateway}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    {/* Sem este aviso, alguém configura contingência de boleto
                      achando que ela atua — só o roteamento inteligente de
                      cartão lê o campo. */}
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <TriangleAlertIcon
                        className="size-3.5 shrink-0 text-amber-500"
                        aria-hidden="true"
                      />
                      {t(
                        `${panelPrefix}.create.fallbackHint`
                      )}
                    </p>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t pt-4">
          <Button
            type="button"
            variant="ghost"
            disabled={
              step === 0 || createMutation.isPending
            }
            onClick={() => setStep(step - 1)}
          >
            {t(`${panelPrefix}.create.back`)}
          </Button>
          {step < TOTAL_STEPS - 1 ? (
            <Button
              type="button"
              disabled={!canAdvance}
              onClick={() => setStep(step + 1)}
            >
              {t(`${panelPrefix}.create.next`)}
            </Button>
          ) : (
            <Button
              type="button"
              disabled={
                !processorId || createMutation.isPending
              }
              onClick={handleCreate}
            >
              {t(`${panelPrefix}.create.submit`)}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
